import { useState, useEffect, useRef, useCallback } from "react";
import { qrBadgeSvg, MARK_CSS_WIDTH, UMAPS_BADGE_URL } from "./utils/qrBadge.js";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_MAPS_KEY;
const API_BASE = process.env.NODE_ENV === "development" ? "http://localhost:3001" : "";
// Nav graph keys for server-side pathfinding (served via /api/pathfind and /api/navgraph)
const EITC_NAV_KEYS = ['nav_eitc_e1', 'nav_eitc_e2', 'nav_eitc_e3', 'nav_stanley_pauley_centre', 'nav_stanley_pauley_engineering'];
// Building polygon keys for server-side serving via /api/building
const BUILDING_KEYS = [
  { key: 'eitc_e1', building: 'e1' },
  { key: 'eitc_e2', building: 'e2' },
  { key: 'eitc_e3', building: 'e3' },
  { key: 'stanley_pauley_centre', building: 'stanley_pauley_centre' },
  { key: 'stanley_pauley_engineering', building: 'stanley_pauley_engineering' },
];

const EITC_CENTER = { lat: 49.8085709, lng: -97.1340870 };

const START_NODE_ID = "id_1773888584940_803";
const START_LABEL = "Atrium";
const ATRIUM_POS = { lat: 49.80855113279848, lng: -97.13392378642376 };

const E1_CENTROID = { lat: 49.80833, lng: -97.13352 };
const E2_CENTROID = { lat: 49.80878, lng: -97.13340 };
const E3_CENTROID = { lat: 49.80852, lng: -97.13460 };
const SPC_CENTROID = { lat: 49.80804, lng: -97.13402 };
const SPE_CENTROID = { lat: 49.80788, lng: -97.13438 };

// ── Utilities ─────────────────────────────────────────────────────────────────

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve) => {
    if (window.google && window.google.maps) { resolve(); return; }
    const existing = document.getElementById("gmap-script");
    if (existing) { existing.addEventListener("load", resolve); return; }
    const script = document.createElement("script");
    script.id = "gmap-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.onload = resolve;
    document.head.appendChild(script);
  });
}

function haversineMeters(n1, n2) {
  const R = 6371000;
  const φ1 = n1.lat * Math.PI / 180, φ2 = n2.lat * Math.PI / 180;
  const Δφ = (n2.lat - n1.lat) * Math.PI / 180;
  const Δλ = (n2.lng - n1.lng) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function clusterPoints(points, radiusM = 22) {
  const clusters = [], assigned = new Set();
  for (let i = 0; i < points.length; i++) {
    if (assigned.has(i)) continue;
    const cluster = [points[i]]; assigned.add(i);
    for (let j = i + 1; j < points.length; j++) {
      if (!assigned.has(j) && haversineMeters(points[i], points[j]) < radiusM) {
        cluster.push(points[j]); assigned.add(j);
      }
    }
    clusters.push({
      lat: cluster.reduce((s, p) => s + p.lat, 0) / cluster.length,
      lng: cluster.reduce((s, p) => s + p.lng, 0) / cluster.length,
    });
  }
  return clusters;
}

const isOutdoorFloor = (f) => f === 0 || f === "outdoor" || f == null;

const TYPE_STYLES = {
  outline:        { fillColor: "#ffffff", fillOpacity: 1,    strokeColor: "#aaaaaa", strokeOpacity: 1,   strokeWeight: 2,   zIndex: 0 },
  room:           { fillColor: "#FCF8EF", fillOpacity: 1,    strokeColor: "#dbdbdb", strokeOpacity: 0.9, strokeWeight: 1.5, zIndex: 1 },
  elevatorstairs: { fillColor: "#9e9e9e", fillOpacity: 1,    strokeColor: "#616161", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  stair:          { fillColor: "#c4956a", fillOpacity: 1,    strokeColor: "#a0724a", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  elevator:       { fillColor: "#b0bec5", fillOpacity: 1,    strokeColor: "#78909c", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  corridor:       { fillColor: "#f5f5f5", fillOpacity: 1,    strokeColor: "#cccccc", strokeOpacity: 0.8, strokeWeight: 1,   zIndex: 1 },
  washroom:       { fillColor: "#e3f2fd", fillOpacity: 1,    strokeColor: "#90caf9", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  other:          { fillColor: "#eeeeee", fillOpacity: 0.85, strokeColor: "#bdbdbd", strokeOpacity: 0.8, strokeWeight: 1,   zIndex: 1 },
};
function getStyle(type) { return TYPE_STYLES[type] || TYPE_STYLES.other; }

// ── SVG helpers ───────────────────────────────────────────────────────────────

function svgUrl(svg) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function stairMarkerIcon() {
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="58" height="26"><rect width="58" height="26" rx="6" fill="#5d4037" opacity="0.9"/><text x="29" y="17" font-size="11" font-family="Arial,sans-serif" font-weight="bold" fill="white" text-anchor="middle">STAIRS</text></svg>`),
    scaledSize: new window.google.maps.Size(58, 26),
    anchor: new window.google.maps.Point(29, 13),
  };
}

function elevatorMarkerIcon() {
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="70" height="26"><rect width="70" height="26" rx="6" fill="#37474f" opacity="0.9"/><text x="35" y="17" font-size="11" font-family="Arial,sans-serif" font-weight="bold" fill="white" text-anchor="middle">ELEVATOR</text></svg>`),
    scaledSize: new window.google.maps.Size(70, 26),
    anchor: new window.google.maps.Point(35, 13),
  };
}

function buildingLabelIcon(label, color) {
  const w = label.length * 9 + 28, h = 30;
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect width="${w}" height="${h}" rx="7" fill="${color}" opacity="0.95"/><text x="${w / 2}" y="20" font-size="12" font-family="Arial,sans-serif" font-weight="bold" fill="white" text-anchor="middle">${label}</text></svg>`),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(w / 2, h / 2),
  };
}

function atriumLabelIcon() {
  const w = 90, h = 34;
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
      <rect x="1.5" y="1.5" width="${w - 3}" height="${h - 3}" rx="7" fill="#fff" fill-opacity="0.97" stroke="#1a73e8" stroke-width="2.5"/>
      <text x="${w / 2}" y="22" font-size="13" font-family="Arial,sans-serif" font-weight="800" fill="#1a73e8" text-anchor="middle" letter-spacing="1">ATRIUM</text>
    </svg>`),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(w / 2, h / 2),
  };
}

function endRoomMarkerIcon(roomNum) {
  const txt = String(roomNum);
  const w = Math.max(68, txt.length * 13 + 28), h = 56;
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
      <rect x="2" y="2" width="${w - 4}" height="38" rx="11" fill="#2e7d32" stroke="white" stroke-width="2.5"/>
      <text x="${w / 2}" y="16" font-size="9" font-family="Arial,sans-serif" fill="rgba(255,255,255,0.75)" text-anchor="middle" font-weight="600" letter-spacing="0.5">DESTINATION</text>
      <text x="${w / 2}" y="34" font-size="20" font-family="Arial,sans-serif" font-weight="bold" fill="white" text-anchor="middle">${txt}</text>
      <polygon points="${w / 2 - 7},40 ${w / 2 + 7},40 ${w / 2},53" fill="#2e7d32"/>
    </svg>`),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(w / 2, h),
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function EITCPage() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const navGraphRef = useRef(null);
  // buildingPolysRef entries: { poly, floor, building, room_number, origFill, origOpacity, origStroke }
  const buildingPolysRef = useRef([]);
  const routeSegmentsRef = useRef([]);
  const routeTransitionsRef = useRef([]);
  const routeMarkersRef = useRef([]);
  const landmarkMarkersRef = useRef([]);
  const FloorTransitionOverlayRef = useRef(null);
  const landmarkDataRef = useRef(null);
  const destPolyHighlightRef = useRef([]);

  const [mapReady, setMapReady] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(2);
  const [query, setQuery] = useState("");
  const [rooms, setRooms] = useState([]);  // all rooms with .building tag
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  const [routeFloors, setRouteFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [destRoom, setDestRoom] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [startRoom, setStartRoom] = useState({ room_number: START_LABEL, node_ids: [START_NODE_ID], floors: new Set([2]), building: null });
  const [startQuery, setStartQuery] = useState(START_LABEL);
  const [startDropdownOpen, setStartDropdownOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [accessibleMode, setAccessibleMode] = useState(false);
  const inputRef = useRef(null);
  const startInputRef = useRef(null);
  const urlParamsLoadedRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Init map ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function init() {
      await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
      if (cancelled) return;

      const map = new window.google.maps.Map(mapRef.current, {
        center: EITC_CENTER,
        zoom: 19,
        mapTypeId: "satellite",
        tilt: 0,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "greedy",
      });
      mapInstanceRef.current = map;

      class FloorTransitionOverlay extends window.google.maps.OverlayView {
        constructor(position, fromFloor, toFloor, transType, onClick) {
          super();
          this.position = position; this.fromFloor = fromFloor;
          this.toFloor = toFloor; this.transType = transType;
          this.onClick = onClick; this._div = null;
        }
        onAdd() {
          const div = document.createElement("div");
          const isUp = this.toFloor > this.fromFloor;
          div.style.cssText = [
            "position:absolute", "background:#fff", "border-radius:10px",
            "padding:7px 14px", "font-family:Arial,sans-serif",
            "box-shadow:0 2px 10px rgba(0,0,0,0.28)", "transform:translate(-50%,-50%)",
            "cursor:pointer", "text-align:center", "border:2px solid #1a73e8",
            "pointer-events:auto", "user-select:none",
          ].join(";");
          div.innerHTML = `<div style="font-size:12px;font-weight:700;color:#1a73e8;white-space:nowrap">${this.transType} ${isUp ? "▲" : "▼"}</div><div style="font-size:11px;font-weight:600;color:#444;margin-top:1px">F${this.fromFloor} → F${this.toFloor}</div><div style="font-size:9px;color:#888;margin-top:2px">tap to switch</div>`;
          div.addEventListener("click", (e) => { e.stopPropagation(); this.onClick(this.toFloor); });
          this._div = div;
          this.getPanes().overlayMouseTarget.appendChild(div);
        }
        draw() {
          const pos = this.getProjection().fromLatLngToDivPixel(this.position);
          if (pos && this._div) { this._div.style.left = pos.x + "px"; this._div.style.top = pos.y + "px"; }
        }
        onRemove() { if (this._div) { this._div.parentNode?.removeChild(this._div); this._div = null; } }
      }
      FloorTransitionOverlayRef.current = FloorTransitionOverlay;

      // Load both building polygon files — tag each poly with its building
      try {
        const results = await Promise.all(
          BUILDING_KEYS.map(({ key, building }) => fetch(`${API_BASE}/api/building?key=${encodeURIComponent(key)}`).then(r => r.json()).then(data => ({ data, building })))
        );
        results.forEach(({ data, building }) => {
          (data.polygons || []).forEach(item => {
            if (!item.coordinates?.length) return;
            const type = item.type?.toLowerCase() || "other";
            const style = getStyle(type);
            const floor = item.floor ?? 1;
            const paths = item.coordinates.map(c => ({ lat: c.latitude, lng: c.longitude }));
            const poly = new window.google.maps.Polygon({
              paths, ...style, clickable: false,
              map: floor === 2 ? map : null,
            });
            buildingPolysRef.current.push({
              poly, floor, building,
              room_number: item.room_number ? String(item.room_number) : null,
              room_name: item.room_name || null,
              origFill: style.fillColor,
              origOpacity: style.fillOpacity,
              origStroke: style.strokeColor,
            });
          });
        });
      } catch (e) { console.error("Failed to load building polygons", e); }

      setMapReady(true);
    }
    init();
    return () => { cancelled = true; };
  }, []);

  // ── Load nav graphs (merged) ──────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;

    fetch(`${API_BASE}/api/navgraph?keys=${EITC_NAV_KEYS.join(',')}`)
      .then(r => r.json())
      .then(data => {
        const merged = {
          nodes: data.nodes || [],
          rooms: data.rooms || [],
        };
        navGraphRef.current = merged;

        const nodes = merged.nodes;
        const nodeTypeMap = Object.fromEntries(nodes.map(n => [n.id, n.type]));

        // Build room_name lookup from already-loaded building polygons
        const polyNameLookup = {};
        buildingPolysRef.current.forEach(p => {
          if (p.room_name && p.room_number) polyNameLookup[`${p.building}:${p.room_number}`] = p.room_name;
        });

        // Build room list with building tag
        const roomMap = {};
        nodes.forEach(n => {
          const rn = n.room_number;
          if (!rn) return;
          const key = `${n.building}:${rn}`;
          if (!roomMap[key]) roomMap[key] = { room_number: String(rn), building: n.building, node_ids: [], floors: new Set(), room_name: polyNameLookup[key] || null };
          roomMap[key].node_ids.push(n.id);
          roomMap[key].floors.add(n.floor);
        });
        const allRooms = Object.values(roomMap).filter(r => {
          if (r.room_number === START_LABEL) return false;
          return !r.node_ids.every(id => ["stair", "elevator"].includes(nodeTypeMap[id]));
        });
        allRooms.sort((a, b) => {
          const af = Math.min(...[...a.floors].filter(Number.isFinite)) || 1;
          const bf = Math.min(...[...b.floors].filter(Number.isFinite)) || 1;
          if (af !== bf) return af - bf;
          return String(a.room_number).localeCompare(String(b.room_number), undefined, { numeric: true });
        });
        setRooms(allRooms);

        // Landmark data — collect per building so stair clusters are separate
        const stairsByFloor = {}, elevatorByFloor = {};
        nodes.forEach(n => {
          if (n.type === "stair" && n.floor) {
            if (!stairsByFloor[n.floor]) stairsByFloor[n.floor] = [];
            stairsByFloor[n.floor].push({ lat: n.lat, lng: n.lng });
          }
          if (n.type === "elevator" && n.floor) {
            elevatorByFloor[n.floor] = { lat: n.lat, lng: n.lng };
          }
        });
        const stairClustersByFloor = {};
        Object.entries(stairsByFloor).forEach(([floor, pts]) => {
          stairClustersByFloor[Number(floor)] = clusterPoints(pts);
        });

        landmarkDataRef.current = { stairClustersByFloor, elevatorByFloor };
        placeLandmarkMarkers(map, 2, stairClustersByFloor, elevatorByFloor);
      })
      .catch(e => console.error("Failed to load nav graphs", e));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  function placeLandmarkMarkers(map, floor, stairClustersByFloor, elevatorByFloor) {
    landmarkMarkersRef.current.forEach(m => m.setMap(null));
    landmarkMarkersRef.current = [];

    (stairClustersByFloor[floor] || []).forEach(pos => {
      landmarkMarkersRef.current.push(new window.google.maps.Marker({
        position: pos, map, icon: stairMarkerIcon(), zIndex: 50, clickable: false,
      }));
    });

    if (elevatorByFloor[floor]) {
      landmarkMarkersRef.current.push(new window.google.maps.Marker({
        position: elevatorByFloor[floor], map, icon: elevatorMarkerIcon(), zIndex: 50, clickable: false,
      }));
    }

    landmarkMarkersRef.current.push(new window.google.maps.Marker({
      position: E1_CENTROID, map, icon: buildingLabelIcon("EITC E1", "#2e7d32"), zIndex: 60, clickable: false,
    }));
    landmarkMarkersRef.current.push(new window.google.maps.Marker({
      position: E2_CENTROID, map, icon: buildingLabelIcon("EITC E2", "#1565c0"), zIndex: 60, clickable: false,
    }));
    landmarkMarkersRef.current.push(new window.google.maps.Marker({
      position: E3_CENTROID, map, icon: buildingLabelIcon("EITC E3", "#6a1b9a"), zIndex: 60, clickable: false,
    }));
    landmarkMarkersRef.current.push(new window.google.maps.Marker({
      position: SPC_CENTROID, map, icon: buildingLabelIcon("SP Centre", "#cc79a7"), zIndex: 60, clickable: false,
    }));
    landmarkMarkersRef.current.push(new window.google.maps.Marker({
      position: SPE_CENTROID, map, icon: buildingLabelIcon("SP Engineering", "#d55e00"), zIndex: 60, clickable: false,
    }));
    // Atrium label at the start staircase
    landmarkMarkersRef.current.push(new window.google.maps.Marker({
      position: ATRIUM_POS, map, icon: atriumLabelIcon(), zIndex: 70, clickable: false,
    }));
  }

  // ── Floor change: show only polys on the selected floor ───────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    buildingPolysRef.current.forEach(({ poly, floor }) => {
      poly.setMap(floor === selectedFloor ? map : null);
    });

    routeSegmentsRef.current.forEach(({ polyline, floor }) => {
      const active = floor === selectedFloor || isOutdoorFloor(floor);
      polyline.setOptions({
        strokeOpacity: active ? 0.9 : 0.3,
        strokeWeight: active ? 7 : 3,
        zIndex: active ? 500 : 400,
      });
    });

    routeTransitionsRef.current.forEach(({ overlay, fromFloor }) => {
      overlay.setMap(fromFloor === selectedFloor ? map : null);
    });

    if (landmarkDataRef.current) {
      const { stairClustersByFloor, elevatorByFloor } = landmarkDataRef.current;
      placeLandmarkMarkers(map, selectedFloor, stairClustersByFloor, elevatorByFloor);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFloor]);

  function highlightDestRoom(roomNumber, building) {
    destPolyHighlightRef.current.forEach(({ poly, origFill, origOpacity, origStroke }) => {
      poly.setOptions({ fillColor: origFill, fillOpacity: origOpacity, strokeColor: origStroke, strokeWeight: 1.5 });
    });
    destPolyHighlightRef.current = [];
    const matches = buildingPolysRef.current.filter(p =>
      p.room_number === String(roomNumber) && (!building || p.building === building)
    );
    matches.forEach(item => {
      item.poly.setOptions({ fillColor: "#00e676", fillOpacity: 0.75, strokeColor: "#00c853", strokeWeight: 3 });
      destPolyHighlightRef.current.push(item);
    });
  }

  function clearDestHighlight() {
    destPolyHighlightRef.current.forEach(({ poly, origFill, origOpacity, origStroke }) => {
      poly.setOptions({ fillColor: origFill, fillOpacity: origOpacity, strokeColor: origStroke, strokeWeight: 1.5 });
    });
    destPolyHighlightRef.current = [];
  }

  const clearRoute = useCallback(() => {
    routeSegmentsRef.current.forEach(({ polyline }) => polyline.setMap(null));
    routeSegmentsRef.current = [];
    routeTransitionsRef.current.forEach(({ overlay }) => overlay.setMap(null));
    routeTransitionsRef.current = [];
    routeMarkersRef.current.forEach(m => m.setMap(null));
    routeMarkersRef.current = [];
    clearDestHighlight();
    setRouteResult(null);
    setRouteFloors([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = useCallback(() => {
    clearRoute();
    setQuery("");
    setDestRoom(null);
    setDropdownOpen(false);
    setStartQuery(START_LABEL);
    setStartRoom({ room_number: START_LABEL, node_ids: [START_NODE_ID], floors: new Set([2]), building: null });
    setStartDropdownOpen(false);
    setSelectedFloor(2);
    window.history.replaceState({}, '', window.location.pathname);
    const map = mapInstanceRef.current;
    if (map) { map.setCenter(EITC_CENTER); map.setZoom(19); }
  }, [clearRoute]);

  // ── Pathfinding ───────────────────────────────────────────────────────────────
  const runRoute = useCallback(async (startRoomObj, destRoomObj, accessible = false) => {
    const graph = navGraphRef.current;
    const map = mapInstanceRef.current;
    if (!graph || !map) return;

    clearRoute();
    setLoading(true);

    const nodeMap = Object.fromEntries((graph.nodes || []).map(n => [n.id, n]));

    let bestPath = null, bestDist = Infinity;
    for (const startId of startRoomObj.node_ids) {
      for (const endId of destRoomObj.node_ids) {
        if (startId === endId) continue;
        const candidateNodes = await fetch(`${API_BASE}/api/pathfind`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ navKeys: EITC_NAV_KEYS, startId, endId, accessibleOnly: accessible }),
        }).then(r => r.json());
        if (!Array.isArray(candidateNodes) || candidateNodes.length < 2) continue;
        let d = 0;
        for (let i = 0; i < candidateNodes.length - 1; i++) {
          d += haversineMeters(candidateNodes[i], candidateNodes[i + 1]);
        }
        if (d < bestDist) {
          bestDist = d;
          bestPath = candidateNodes.map(n => n.id);
          candidateNodes.forEach(n => { if (!nodeMap[n.id]) nodeMap[n.id] = n; });
        }
      }
    }

    setLoading(false);
    if (!bestPath) { setRouteResult("not_found"); return; }

    const path = bestPath;
    const startFloor = nodeMap[path[0]]?.floor ?? 2;

    const rawSegments = [], rawTransitions = [];
    let segFloor = nodeMap[path[0]]?.floor ?? 2, segCoords = [];
    for (let i = 0; i < path.length; i++) {
      const node = nodeMap[path[i]];
      const nodeFloor = node?.floor ?? 2;
      if (nodeFloor !== segFloor) {
        const bridgeCoord = segCoords[segCoords.length - 1];
        rawSegments.push({ floor: segFloor, coords: segCoords });
        if (!isOutdoorFloor(segFloor) && !isOutdoorFloor(nodeFloor)) {
          const prevNode = nodeMap[path[i - 1]];
          const transType = prevNode?.type === "elevator" ? "Elevator" : "Stairs";
          const pos = new window.google.maps.LatLng(bridgeCoord.lat, bridgeCoord.lng);
          rawTransitions.push({ fromFloor: segFloor, toFloor: nodeFloor, position: pos, transType });
          rawTransitions.push({ fromFloor: nodeFloor, toFloor: segFloor, position: pos, transType });
        }
        segFloor = nodeFloor;
        segCoords = [bridgeCoord];
      }
      segCoords.push({ lat: node.lat, lng: node.lng });
    }
    rawSegments.push({ floor: segFloor, coords: segCoords });

    rawSegments.forEach(({ floor, coords }) => {
      const active = floor === startFloor || isOutdoorFloor(floor);
      const polyline = new window.google.maps.Polyline({
        path: coords, strokeColor: "#00c853",
        strokeOpacity: active ? 0.9 : 0.3,
        strokeWeight: active ? 7 : 3,
        zIndex: active ? 500 : 400, map,
      });
      routeSegmentsRef.current.push({ polyline, floor });
    });

    const FTO = FloorTransitionOverlayRef.current;
    if (FTO) {
      rawTransitions.forEach(({ fromFloor, toFloor, transType, position }) => {
        const overlay = new FTO(position, fromFloor, toFloor, transType, (f) => setSelectedFloor(f));
        overlay.setMap(fromFloor === startFloor ? map : null);
        routeTransitionsRef.current.push({ overlay, fromFloor });
      });
    }

    const sNode = nodeMap[path[0]];
    routeMarkersRef.current.push(new window.google.maps.Marker({
      position: { lat: sNode.lat, lng: sNode.lng }, map,
      icon: { path: window.google.maps.SymbolPath.CIRCLE, fillColor: "#1a73e8", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3, scale: 10 },
      zIndex: 600, title: START_LABEL,
    }));

    const eNode = nodeMap[path[path.length - 1]];
    routeMarkersRef.current.push(new window.google.maps.Marker({
      position: { lat: eNode.lat, lng: eNode.lng }, map,
      icon: endRoomMarkerIcon(destRoomObj.room_number),
      zIndex: 600, title: `Room ${destRoomObj.room_number}`,
    }));

    highlightDestRoom(destRoomObj.room_number, destRoomObj.building);

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(id => { const n = nodeMap[id]; if (n) bounds.extend({ lat: n.lat, lng: n.lng }); });
    map.fitBounds(bounds, 100);

    const involvedFloors = [...new Set(path.map(id => nodeMap[id]?.floor).filter(Boolean))].sort();
    setRouteFloors(involvedFloors);
    setSelectedFloor(startFloor);
    setRouteResult({ meters: Math.round(bestDist) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearRoute]);

  const handleSelectRoom = useCallback((room) => {
    setDestRoom(room);
    setQuery(roomLabel(room));
    setDropdownOpen(false);
    // Switch to the destination room's floor immediately so the overlay is correct
    const destFloor = Math.min(...[...room.floors].filter(Number.isFinite));
    if (Number.isFinite(destFloor)) setSelectedFloor(destFloor);
    runRoute(startRoom, room, accessibleMode);
  }, [runRoute, startRoom, accessibleMode]);

  const handleSelectStartRoom = useCallback((room) => {
    setStartRoom(room);
    setStartQuery(room.building ? roomLabel(room) : room.room_number);
    setStartDropdownOpen(false);
    if (destRoom) runRoute(room, destRoom, accessibleMode);
  }, [runRoute, destRoom, accessibleMode]);

  const handleAccessibleToggle = useCallback(() => {
    const next = !accessibleMode;
    setAccessibleMode(next);
    if (destRoom) runRoute(startRoom, destRoom, next);
  }, [accessibleMode, destRoom, startRoom, runRoute]);

  const roomLabel = (room) => room.building ? `${room.building.toUpperCase()} ${room.room_number}` : room.room_number;

  // Filter rooms for TO box — search across both buildings, show as "E2 XXX" / "E3 XXX"
  const filteredRooms = (() => {
    const q = query.toLowerCase().trim();
    if (!q) return rooms;
    return rooms.filter(r => {
      const label = `${r.building} ${r.room_number}`.toLowerCase();
      const roomName = (r.room_name || "").toLowerCase();
      return label.includes(q) || String(r.room_number).toLowerCase().includes(q) || roomName.includes(q);
    });
  })();

  // Filter rooms for FROM box — Atrium first, then all rooms
  const atriumEntry = { room_number: START_LABEL, node_ids: [START_NODE_ID], floors: new Set([2]), building: null };
  const filteredStartRooms = (() => {
    const q = startQuery.toLowerCase().trim();
    const roomMatches = rooms.filter(r => {
      const label = `${r.building} ${r.room_number}`.toLowerCase();
      const roomName = (r.room_name || "").toLowerCase();
      return label.includes(q) || String(r.room_number).toLowerCase().includes(q) || roomName.includes(q);
    });
    return START_LABEL.toLowerCase().includes(q) ? [atriumEntry, ...roomMatches] : roomMatches;
  })();

  const metersToDisplay = (m) => m < 100 ? `${m} m` : `${(m / 1000).toFixed(2)} km`;
  const hasRoute = routeResult || loading || destRoom;

  // ── URL share state ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!destRoom) return;
    const url = new URL(window.location.href);
    url.searchParams.set('to', roomLabel(destRoom));
    if (startRoom.building) url.searchParams.set('from', roomLabel(startRoom));
    else url.searchParams.delete('from');
    window.history.replaceState({}, '', url.toString());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destRoom, startRoom]);

  // Auto-load route from URL params once rooms are ready
  useEffect(() => {
    if (rooms.length === 0 || urlParamsLoadedRef.current) return;
    urlParamsLoadedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const toParam = params.get('to');
    if (!toParam) return;
    const [bldg, ...rest] = toParam.split(' ');
    const roomNum = rest.join(' ');
    const toRoom = rooms.find(r => r.building?.toUpperCase() === bldg.toUpperCase() && String(r.room_number) === roomNum);
    if (!toRoom) return;
    const fromParam = params.get('from');
    let fromRoom = atriumEntry;
    if (fromParam) {
      const [fb, ...fr] = fromParam.split(' ');
      const frn = fr.join(' ');
      const found = rooms.find(r => r.building?.toUpperCase() === fb.toUpperCase() && String(r.room_number) === frn);
      if (found) { fromRoom = found; setStartRoom(found); setStartQuery(roomLabel(found)); }
    }
    setDestRoom(toRoom);
    setQuery(roomLabel(toRoom));
    runRoute(fromRoom, toRoom, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);

  // ── JSX ───────────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, fontFamily: "Arial, sans-serif" }}>

      {/* Map */}
      <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />

      {/* Top gradient */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        height: isMobile ? 200 : 150,
        background: "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, transparent 100%)",
        pointerEvents: "none", zIndex: 10,
      }} />

      {/* ── Header: logo + cancel — left/right edges, independent of search ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, zIndex: 20,
        padding: isMobile ? "10px 12px" : "12px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        pointerEvents: "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, pointerEvents: "all" }}>
          <img src="/UMaps Logo Circle Border.png" alt="UMaps"
            style={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, borderRadius: "50%", flexShrink: 0 }}
            onError={e => e.target.style.display = "none"} />
          <div>
            <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: "#fff", lineHeight: 1.2 }}><b style={{ fontWeight: 800 }}>UMaps</b> EITC E1, E2 &amp; E3 Pathfinder</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={() => setAboutOpen(true)} style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                fontSize: 10, color: "rgba(255,255,255,0.75)", fontWeight: 600,
                textDecoration: "underline",
              }}>about ›</button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, pointerEvents: "all" }}>
          {/* Floor selector — horizontal in header */}
          {routeFloors.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(0,0,0,0.55)", borderRadius: 10, padding: "4px 6px", backdropFilter: "blur(8px)" }}>
              {routeFloors.map(f => (
                <button key={f} onClick={() => setSelectedFloor(f)} style={{
                  width: 32, height: 32, borderRadius: 8, border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: 800,
                  background: selectedFloor === f ? "#00c853" : "rgba(255,255,255,0.15)",
                  color: selectedFloor === f ? "#fff" : "rgba(255,255,255,0.7)",
                  transition: "background 0.15s",
                  WebkitTapHighlightColor: "transparent",
                }}>{f}</button>
              ))}
            </div>
          )}
          {hasRoute && (
            <button onClick={handleCancel} style={{
              padding: isMobile ? "7px 13px" : "8px 18px",
              borderRadius: 10, border: "none", cursor: "pointer",
              background: "#e53935", color: "#fff",
              fontSize: isMobile ? 12 : 13, fontWeight: 800,
              boxShadow: "0 3px 14px rgba(229,57,53,0.5)",
            }}>✕ CANCEL</button>
          )}
        </div>
      </div>

      {/* ── Search bars — stacked, centered, below header ── */}
      <div style={{
        position: "absolute",
        top: isMobile ? 70 : 72,
        left: isMobile ? 12 : "50%",
        right: isMobile ? 12 : "auto",
        transform: isMobile ? "none" : "translateX(-50%)",
        width: isMobile ? "auto" : 400,
        zIndex: 30,
        display: "flex", flexDirection: "column", gap: 6,
      }}>
        <SearchBox
          label="FROM" query={startQuery} setQuery={setStartQuery}
          dropdownOpen={startDropdownOpen} setDropdownOpen={setStartDropdownOpen}
          filteredRooms={filteredStartRooms} handleSelectRoom={handleSelectStartRoom}
          inputRef={startInputRef} isMobile={isMobile} placeholder={START_LABEL}
        />
        {!startDropdownOpen && (
          <SearchBox
            label="TO" query={query} setQuery={setQuery}
            dropdownOpen={dropdownOpen} setDropdownOpen={setDropdownOpen}
            filteredRooms={filteredRooms} handleSelectRoom={handleSelectRoom}
            inputRef={inputRef} isMobile={isMobile} placeholder="Search E1, E2 or E3 room…"
          />
        )}
      </div>

      {/* ── Bottom: single unified dark container ── */}
      {routeResult && routeResult !== "not_found" && (
        <div style={{
          position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 20,
          background: "rgba(30,30,30,0.92)", borderRadius: 20,
          padding: "16px 18px",
          backdropFilter: "blur(12px)", boxShadow: "0 4px 28px rgba(0,0,0,0.55)",
          display: "flex", flexDirection: "column", gap: 10,
          width: isMobile ? "calc(100vw - 32px)" : 400,
        }}>
          {/* Route info row */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>🟢</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {startRoom.room_number} → {roomLabel(destRoom)}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                ~{metersToDisplay(routeResult.meters)}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

          {/* Accessibility toggle */}
          <button onClick={handleAccessibleToggle} style={{
            width: "100%", padding: "9px 0", borderRadius: 11, border: "none", cursor: "pointer",
            background: accessibleMode ? "#1565c0" : "rgba(255,255,255,0.1)",
            color: "#fff", fontWeight: 700, fontSize: 13,
            transition: "background 0.2s", letterSpacing: "0.02em",
          }}>
            ♿ {accessibleMode ? "Accessible Mode (Elevators & Ramps)" : "Accessible Mode"}
          </button>

          {/* Share + QR */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => navigator.clipboard?.writeText(window.location.href).then(() => { setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); })}
              style={{
                flex: 1, padding: "9px 0", borderRadius: 11, border: "none", cursor: "pointer",
                background: shareCopied ? "#2e7d32" : "rgba(255,255,255,0.1)",
                color: "#fff", fontWeight: 700, fontSize: 13, transition: "background 0.2s",
              }}
            >{shareCopied ? "✓ Copied!" : "🔗 Share Link"}</button>
            <button
              onClick={() => setShowQR(true)}
              style={{
                flex: 1, padding: "9px 0", borderRadius: 11, border: "none", cursor: "pointer",
                background: "rgba(255,255,255,0.1)", color: "#fff", fontWeight: 700, fontSize: 13,
              }}
            >📱 Show QR</button>
          </div>
        </div>
      )}

      {routeResult === "not_found" && (
        <div style={{
          position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 20, background: "rgba(229,57,53,0.92)", borderRadius: 14,
          padding: "11px 22px", color: "#fff", fontWeight: 700, fontSize: 14,
          boxShadow: "0 4px 20px rgba(0,0,0,0.4)", maxWidth: "calc(100vw - 32px)",
          textAlign: "center",
        }}>
          No route found to {destRoom ? roomLabel(destRoom) : "room"}
          {accessibleMode && <div style={{ fontSize: 11, fontWeight: 400, marginTop: 4, opacity: 0.85 }}>Try disabling Accessible Mode</div>}
        </div>
      )}

      {loading && (
        <div style={{
          position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 20, background: "rgba(0,0,0,0.84)", borderRadius: 14,
          padding: "12px 26px", color: "#fff", fontSize: 14, fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
        }}>Finding route…</div>
      )}

      {/* ── About popup ───────────────────────────────────────────────────────── */}
      {aboutOpen && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setAboutOpen(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 20, padding: "28px 24px", maxWidth: 340, width: "100%", boxShadow: "0 16px 56px rgba(0,0,0,0.3)", textAlign: "center" }}
            onClick={e => e.stopPropagation()}
          >
            <img src="/UMaps Logo Circle Border.png" alt="UMaps" style={{ width: 56, height: 56, borderRadius: "50%", marginBottom: 14 }} onError={e => e.target.style.display = "none"} />
            <div style={{ fontSize: 14, color: "#444", lineHeight: 1.7, marginBottom: 16 }}>
              UMaps is created by <strong>Quadric Francisco</strong> in order to help students at the UofM navigate easier.<br /><br />
              To contact Quadric, please e-mail{" "}
              <a href="mailto:franci44@myumanitoba.ca" style={{ color: "#1a73e8", fontWeight: 600, textDecoration: "none" }}>franci44@myumanitoba.ca</a>. :)
            </div>
            <button onClick={() => setAboutOpen(false)} style={{ padding: "9px 28px", borderRadius: 10, border: "none", cursor: "pointer", background: "#1a73e8", color: "#fff", fontWeight: 700, fontSize: 14 }}>Close</button>
          </div>
        </div>
      )}

      {/* ── QR popup ─────────────────────────────────────────────────────────── */}
      {showQR && destRoom && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.72)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}
          onClick={() => setShowQR(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 22, padding: "28px 28px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, boxShadow: "0 16px 56px rgba(0,0,0,0.5)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>Scan to open this route</div>
            {/* Drawn here, not fetched from api.qrserver.com. That service
                defaults to error-correction level L, which leaves no budget for
                a mark in the middle, and a code rendered by a third party
                cannot carry ours at all. `qrBadgeSvg` is the same generator the
                printed signs, the event posters and the app's share sheet use:
                level M, the white disc and the accent ring drawn into the SVG,
                the mark laid over it. It also means the code still appears when
                the network does not. */}
            <div style={{ position: "relative", width: 220, height: 220 }} role="img" aria-label="QR Code">
              <div style={{ lineHeight: 0 }}
                   dangerouslySetInnerHTML={{ __html: qrBadgeSvg(window.location.href, 220) }} />
              <img
                src={UMAPS_BADGE_URL}
                alt=""
                onError={e => { e.target.style.display = "none"; }}
                style={{
                  position: "absolute", left: "50%", top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: MARK_CSS_WIDTH, height: "auto", display: "block",
                }}
              />
            </div>
            <div style={{ fontSize: 11, color: "#aaa", textAlign: "center", maxWidth: 220 }}>
              {startRoom.room_number} → {roomLabel(destRoom)}
            </div>
            <button onClick={() => setShowQR(false)} style={{ padding: "9px 32px", borderRadius: 10, border: "none", cursor: "pointer", background: "#1a73e8", color: "#fff", fontWeight: 700, fontSize: 14 }}>Close</button>
          </div>
        </div>
      )}

      {(dropdownOpen || startDropdownOpen) && (
        <div style={{ position: "fixed", inset: 0, zIndex: 15 }} onClick={() => { setDropdownOpen(false); setStartDropdownOpen(false); }} />
      )}
    </div>
  );
}

// ── Search box ────────────────────────────────────────────────────────────────

function SearchBox({ label, query, setQuery, dropdownOpen, setDropdownOpen, filteredRooms, handleSelectRoom, inputRef, isMobile, placeholder }) {
  const bldgColor = { e1: "#2e7d32", e2: "#1565c0", e3: "#6a1b9a", stanley_pauley_centre: "#cc79a7", stanley_pauley_engineering: "#d55e00" };
  return (
    <div style={{ position: "relative", zIndex: 25 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        background: "#fff", borderRadius: 14,
        boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
        padding: isMobile ? "10px 12px" : "10px 14px",
      }}>
        {label && (
          <span style={{ fontSize: 9, fontWeight: 800, color: "#aaa", letterSpacing: "0.06em", flexShrink: 0, minWidth: 22 }}>{label}</span>
        )}
        <span style={{ fontSize: 15, flexShrink: 0 }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setDropdownOpen(true); }}
          onFocus={() => setDropdownOpen(true)}
          placeholder={placeholder || "Search room…"}
          style={{
            flex: 1, border: "none", outline: "none", minWidth: 0,
            fontSize: isMobile ? 16 : 14, fontWeight: 600, color: "#222",
            background: "transparent",
          }}
          autoComplete="off"
          inputMode="search"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setDropdownOpen(true); inputRef.current?.focus(); }}
            style={{ border: "none", background: "none", cursor: "pointer", fontSize: 15, color: "#aaa", lineHeight: 1, padding: "2px 4px", flexShrink: 0 }}
          >✕</button>
        )}
      </div>

      {dropdownOpen && filteredRooms.length > 0 && (
        <div
          onMouseDown={e => e.preventDefault()}
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
            background: "#fff", borderRadius: 14,
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            maxHeight: isMobile ? "50vh" : 340,
            overflowY: "auto", zIndex: 200,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {filteredRooms.map(room => {
            const floors = [...(room.floors || [])].filter(Number.isFinite).sort((a, b) => a - b);
            const floorLabel = floors.length === 1 ? `F${floors[0]}` : `F${floors.join("/")}`;
            const bldg = room.building?.toUpperCase();
            const color = room.building ? (bldgColor[room.building] || "#444") : "#1a73e8";
            return (
              <button
                key={`${room.building ?? ""}:${room.room_number}`}
                onClick={() => handleSelectRoom(room)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", textAlign: "left",
                  padding: isMobile ? "12px 16px" : "10px 16px",
                  border: "none", borderBottom: "1px solid #f0f0f0",
                  background: "none", cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {bldg && (
                    <span style={{ fontSize: 10, fontWeight: 800, color, background: `${color}18`, borderRadius: 5, padding: "2px 6px", flexShrink: 0 }}>{bldg}</span>
                  )}
                  <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <span style={{ fontSize: isMobile ? 14 : 13, fontWeight: 600, color: "#222" }}>{room.room_name || room.room_number}</span>
                    {room.room_name && <span style={{ fontSize: 10, color: "#aaa" }}>{room.room_number}</span>}
                  </span>
                </span>
                {room.building && <span style={{ fontSize: 11, color: "#aaa", fontWeight: 400 }}>{floorLabel}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
